import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import SlidingPlayMain from "../../../../admin/story/SlidingPlayMain";
import {
    getImageUrl,
    getStoryTitle,
    getTemplateDifficultyLevel,
    getTemplateGrid,
    getTemplateStartingLayout,
    loadScreenshotSliding,
} from "../../lib/screenshotApi";
import {
    appendScreenshotLang,
    slidingAnimationKey,
    useScreenshotTranslation,
} from "../../../../lib/screenshotI18n";
import { screenshotFontStack, screenshotShellFontSx } from "../../../../lib/screenshotFonts";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../../lib/screenshotReady";
import ScreenshotPromoHeader from "../../components/ScreenshotPromoHeader";
import ScreenshotSizeDifficultyBar from "../../components/ScreenshotSizeDifficultyBar";
import ScreenshotTagBar from "../../components/ScreenshotTagBar";
import { SCREENSHOTS_MAIN_PATH } from "../../routes";
import {
    boardFrameSx,
    boardRegionSx,
    phoneShellSx,
    promoSubtitleSx,
    puzzleTitleBarSx,
    slidingScreenshotAccent,
} from "../../styles/screenshotStyles";
import { animationVideoPath } from "../routes";
import {
    applyMoveAtVisual,
    defaultEmptyTileIndex,
    delay,
    findSolveClickSequenceAsync,
    isSolvedLayout,
    randomDurationMs,
} from "../lib/slidingLayoutMoves";

/**
 * Scene 1 — solve the sliding puzzle, then go to the video page.
 */
export default function AnimationPlayPage({ scene }) {
    const { t, lang } = useScreenshotTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [group, setGroup] = useState(null);
    const [template, setTemplate] = useState(null);
    const [currentLayout, setCurrentLayout] = useState(null);
    const animationRunIdRef = useRef(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await loadScreenshotSliding(scene);
                if (cancelled) return;
                setGroup(result.group);
                setTemplate(result.template);
                const layout = getTemplateStartingLayout(result.template);
                if (layout.length === 0) {
                    setError("Puzzle starting layout is missing.");
                } else {
                    setCurrentLayout(layout);
                    setError(null);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e?.message ?? "Failed to load sliding puzzle");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [scene]);

    const grid = useMemo(() => (template ? getTemplateGrid(template) : 0), [template]);
    const initialLayout = useMemo(() => {
        if (!template || grid <= 0) return null;
        const layout = getTemplateStartingLayout(template);
        return layout.length > 0 ? layout : null;
    }, [template, grid]);
    const imageUrl = getImageUrl(group?.story);
    const title = getStoryTitle(group?.story);
    const puzzleTags = useMemo(() => {
        const fromTemplate = group?.puzzleTemplate?.tags ?? [];
        if (fromTemplate.length > 0) return fromTemplate;
        return scene?.label ? [scene.label] : [];
    }, [group, scene?.label]);

    const emptyIdx = defaultEmptyTileIndex(grid);
    const solveMoveDelayMinMs = scene.solveMoveDelayMinMs ?? 350;
    const solveMoveDelayMaxMs = scene.solveMoveDelayMaxMs ?? 750;
    const slideTransitionMs = scene.slideTransitionMs ?? 160;

    useEffect(() => {
        markScreenshotNotReady();
    }, [scene.slug, lang]);

    useEffect(() => {
        if (loading || error || !template || !imageUrl || grid <= 0 || !currentLayout) {
            return undefined;
        }
        let cancelled = false;
        (async () => {
            await signalScreenshotSceneReady({
                imageUrl,
                fontFamily: screenshotFontStack(lang),
                lang,
            });
            if (cancelled) markScreenshotNotReady();
        })();
        return () => {
            cancelled = true;
        };
    }, [loading, error, template, imageUrl, grid, currentLayout, lang, scene.slug]);

    useEffect(() => {
        if (loading || error || !initialLayout || grid <= 0) {
            return undefined;
        }

        const runId = animationRunIdRef.current + 1;
        animationRunIdRef.current = runId;
        let cancelled = false;

        const runAnimation = async () => {
            let layout = [...initialLayout];
            setCurrentLayout(layout);

            await delay(120);
            if (cancelled || animationRunIdRef.current !== runId) return;

            const solveMoves = await findSolveClickSequenceAsync(layout, grid, emptyIdx);
            if (cancelled || animationRunIdRef.current !== runId) return;

            if (solveMoves && solveMoves.length > 0) {
                for (const visual of solveMoves) {
                    if (cancelled || animationRunIdRef.current !== runId) return;
                    await delay(randomDurationMs(solveMoveDelayMinMs, solveMoveDelayMaxMs));
                    const next = applyMoveAtVisual(layout, visual, grid, emptyIdx);
                    if (!next) continue;
                    layout = next;
                    setCurrentLayout([...layout]);
                    await delay(slideTransitionMs);
                }
            } else if (!isSolvedLayout(layout)) {
                layout = Array.from({ length: grid * grid }, (_, i) => i);
                setCurrentLayout([...layout]);
            }

            if (cancelled || animationRunIdRef.current !== runId) return;
            await delay(scene.pauseAfterSolveMs ?? 400);

            if (!cancelled && animationRunIdRef.current === runId) {
                navigate(appendScreenshotLang(animationVideoPath(scene.slug), lang));
            }
        };

        runAnimation();

        return () => {
            cancelled = true;
        };
    }, [
        loading,
        error,
        initialLayout,
        grid,
        emptyIdx,
        scene.pauseAfterSolveMs,
        scene.slug,
        solveMoveDelayMinMs,
        solveMoveDelayMaxMs,
        slideTransitionMs,
        navigate,
        lang,
    ]);

    if (loading) {
        return (
            <Box sx={{ ...phoneShellSx, alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={32} sx={{ color: slidingScreenshotAccent }} />
            </Box>
        );
    }

    if (error || !template || !imageUrl || grid <= 0 || !currentLayout) {
        return (
            <Box sx={{ ...phoneShellSx, p: 2 }}>
                <Typography color="error" sx={{ mb: 1 }}>
                    {error ?? t("mobileScreenshot.common.could-not-load")}
                </Typography>
                <Link component={RouterLink} to={SCREENSHOTS_MAIN_PATH} sx={{ color: slidingScreenshotAccent }}>
                    {t("mobileScreenshot.common.back-to-index")}
                </Link>
            </Box>
        );
    }

    return (
        <Box sx={{ ...phoneShellSx, ...screenshotShellFontSx(lang) }}>
            <ScreenshotPromoHeader
                promoHeadline={t(slidingAnimationKey(scene.slug, "headline"))}
                promoSubtitle={t(slidingAnimationKey(scene.slug, "subtitle"))}
            />

            <Box sx={boardRegionSx}>
                <Box
                    sx={{
                        ...boardFrameSx,
                        position: "relative",
                        width: "100%",
                        aspectRatio: "1 / 1",
                        overflow: "hidden",
                        bgcolor: "#64748b",
                    }}
                >
                    <SlidingPlayMain
                        imageUrl={imageUrl}
                        gridSize={grid}
                        startingLayout={currentLayout}
                        hideCompletedCaption
                        readOnly
                        playMoveSound={false}
                        slideTransitionMs={slideTransitionMs}
                        maxBoardWidth="100%"
                        fillParent
                    />
                </Box>
            </Box>

            {(title || puzzleTags.length > 0) ? (
                <Box sx={puzzleTitleBarSx}>
                    {title ? (
                        <Typography component="p" sx={promoSubtitleSx}>
                            {title}
                        </Typography>
                    ) : null}
                    <ScreenshotTagBar tags={puzzleTags} />
                </Box>
            ) : null}

            <ScreenshotSizeDifficultyBar
                gridSize={grid}
                difficultyLevel={getTemplateDifficultyLevel(template)}
            />
        </Box>
    );
}
